import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { css } from 'styled-components/native'

import { isLeidosUser } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { FieldSchema } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { FormBody, FormButtonParams } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { eligibilityFormMetadata } from './eligibilityFormMetadata'

interface EligibilityFormProps {
  customers: string[]
  selectedCustomer?: CustomerInfo
  onSubmit({ values }: { values: Dict }): void
  onFormChange?({ values }: { values: Dict }): void
}

export const EligibilityForm: React.FC<EligibilityFormProps> = ({
  customers,
  selectedCustomer,
  onSubmit,
  onFormChange,
}) => {
  const isLeidosSelected = isLeidosUser(selectedCustomer?.value)
  const intl = useIntl()
  const content = eligibilityFormMetadata(customers)
  const { schema, uiSchema } = content ?? {}

  // Update the FormButton to use FormButtonParams instead of FormRenderProps
  const FormButton = ({ handleSubmit, loading }: FormButtonParams) => {
    return (
      <PrimaryButton
        text={<FormattedMessage defaultMessage='Check Coverage' description='Button to check coverage' />}
        onPress={() => handleSubmit()}
        testID={tID('CheckCoverage-Button')}
        accessibilityLabel={intl.formatMessage({
          defaultMessage: 'Check Coverage',
          description: 'Button to check coverage',
        })}
        fullWidth
        loading={loading}
      />
    )
  }

  const initialValues = {
    dependentRow: 0, // Set default to 0 for "No" or 1 for "Yes"
  }

  return (
    <>
      {isLeidosSelected && (
        <InlineWarningBanner
          title='Please check the client profile for session limits.'
          text={`Session limits for ${selectedCustomer?.label} members are not available through this tool. Please check the client's profile for their current session limit.`}
          testId='EligibilityForm-LeidosBanner'
        />
      )}
      <FormBody
        name='eligibityForm'
        schema={schema as FieldSchema}
        uiSchema={uiSchema}
        saveForm={onSubmit}
        onFormChange={onFormChange}
        intl={intl}
        useMultiSelectTypeAhead
        initialValues={initialValues}
        scrollContainerCustomStyles={{
          formBodyPageContainer: css`
            max-width: 100%;
            margin: 0;
            padding: 0;
          `,
          scrollContainerContentCustomStyles: {
            padding: 0,
          },
        }}
        formBodyCustomStyles={{
          submitButtonWrapper: css`
            align-self: end;
            max-width: 180px;
          `,
          submitButtonContainer: css`
            border: none;
            box-shadow: none;
            border-top-width: 0;
            padding: 0;
          `,
        }}
        formButton={isLeidosSelected ? undefined : FormButton}
      />
    </>
  )
}

export default EligibilityForm
